/**
 * A SAVED period against the one on screen.
 *
 * A dashboard and a report both store the period they open on as a macro plus
 * the window it resolved to (`{ preset?, from, to }`), and both let a reader
 * pick another one that is theirs alone. These say whether the two are the
 * same period, and turn the reader's into one that can be saved.
 */
import {
  DATE_RANGE_PRESETS,
  type DateRangeValue,
  resolveDateRange,
} from '@clockwise/shared/date-range';

/** The stored shape — `DashboardPeriod`, and a spec's `period`/`from`/`to`. */
export interface SavedPeriod {
  preset?: (typeof DATE_RANGE_PRESETS)[number];
  from: string;
  to: string;
}

/**
 * Is the reader on the saved period?
 *
 * A MACRO is compared by name: "Last month" saved in August is still "Last
 * month" in September, though its dates moved. Anything else — a custom range,
 * or a saved period with no macro — by its dates.
 */
export function samePeriod(saved: SavedPeriod, current: DateRangeValue): boolean {
  if (saved.preset && saved.preset !== 'custom') return current.preset === saved.preset;
  if (current.preset !== 'custom') return false;
  return current.from === saved.from && current.to === saved.to;
}

/**
 * The reader's period, as it would be saved — or `null` when it cannot be.
 *
 * `null` for a period with no window (All time) and for the two opt-in macros
 * a saved period may not name: storing either would be a period the next
 * reader could not be opened on.
 */
export function toSavedPeriod(value: DateRangeValue, today?: Date): SavedPeriod | null {
  const window = resolveDateRange(value, today);
  if (!window) return null;
  if (!(DATE_RANGE_PRESETS as readonly string[]).includes(value.preset)) return null;
  const preset = value.preset as SavedPeriod['preset'];
  return { preset, from: window.from, to: window.to };
}
